import _ from 'lodash'
import styled from '@emotion/styled'
import { faDiamondTurnRight, faPlay } from '@fortawesome/pro-solid-svg-icons'
import { BranchDesignator } from "@fusion-advantage/backcompat"
import {
	Appointment,
	appointmentMapUrl,
	usPhoneDisplay,
	WorkItemAction,
	workItemTitle,
	DisplayExport,
} from '@fusion-advantage/formlib'
import { faBan, faCircleCheck, faCloudArrowUp, faGlasses } from '@fortawesome/pro-regular-svg-icons'
import { V2L2WorkItemDetailActions, V2L2WorkItemDetailContent, V2L2WorkItemDetailRoot, V2L2WorkItemDetailTitle } from './V2L2WorkItemDetail'
import { L1ReasonableButton } from '../../../form/l1/L1Button'
import { FusionColor } from '../../../../theme/FusionStyle'
import { V2L2WorkItemDetailContentItem, V2L2WorkItemDetailGuidContentItem } from './V2L2WorkItemDetailContentItem'
import { V2L2WorkItemBranchDesignator } from './V2L2WorkItemBranchDesignator'
import { ReminderState, V2L2VideoBobJobber } from './V2L2VideoBobJobber'

const Spacer = styled.div({ flex: 1 })

interface V2L3ApptReadyDetailProps {
	appt: Appointment
	dexp: DisplayExport
	pendingSync: boolean
	canViewForm: boolean
	canStartEncounter: boolean
	canContinueForm: boolean
	canJoinVideo: boolean
	isJoiningVideo: boolean
	canSendReminder: boolean
	videoBobJobberOpen: boolean
	reminderState: ReminderState
	canCancel: boolean
	onActionClick: (action: WorkItemAction) => void
}

export const V2L3ApptReadyDetail = ({
	appt,
	dexp,
	pendingSync,
	canViewForm,
	canStartEncounter,
	canContinueForm,
	canJoinVideo,
	isJoiningVideo,
	canSendReminder,
	videoBobJobberOpen,
	reminderState,
	canCancel,
	onActionClick,
}: V2L3ApptReadyDetailProps) => {
	return (
		<V2L2WorkItemDetailRoot>
			<V2L2WorkItemDetailTitle ready={true}>
				{workItemTitle(appt)}
				<Spacer />
				<V2L2WorkItemBranchDesignator designator={dexp.branchDesignator} />
			</V2L2WorkItemDetailTitle>
			<V2L2WorkItemDetailContent>
				<V2L2WorkItemDetailContentItem label="Age">
					{dexp.memberAge + ' yrs'}
				</V2L2WorkItemDetailContentItem>
				<V2L2WorkItemDetailContentItem label="Clinician Notes" span={2}>
					{appt.ClinicianNotes ?? '—'}
				</V2L2WorkItemDetailContentItem>
				<V2L2WorkItemDetailContentItem label="Client">
					{dexp.clientName ?? '—'}
				</V2L2WorkItemDetailContentItem>
				<V2L2WorkItemDetailContentItem label="Interpreter" span={2}>
					{appt.HasInterpreter ? 'Yes' : 'No'}
					{(appt.HasInterpreter && appt.InterpreterNumber) ? ' — ' + appt.InterpreterNumber : null}
				</V2L2WorkItemDetailContentItem>
				{dexp.branchDesignator !== BranchDesignator.VideoVisit ? (
					<V2L2WorkItemDetailContentItem label="Requested Labs" span={3}>
						{(appt.EligibleLabs && appt.EligibleLabs.length > 0) ? appt.EligibleLabs.join(', ') : '—'}
					</V2L2WorkItemDetailContentItem>
				) : null}
				{/* fill the whole row */}
				{appt.MemberPhones.length > 0 ? [0,1,2].map(idx => _.take(appt.MemberPhones, 2).at(idx)).map((phone, idx) => phone ? (
					<V2L2WorkItemDetailContentItem
						key={idx}
						label={`Phone ${idx+1} ${phone.Label === 'Mobile' ? '(Mobile)' : ''}`}
					>
						{usPhoneDisplay(phone.Number ?? '—')}
					</V2L2WorkItemDetailContentItem>
				) : (
					<V2L2WorkItemDetailContentItem />
				)) : null}
				<V2L2WorkItemDetailContentItem label="Address">
					{appt.Street}<br/>
					{[appt.City, appt.State, appt.PostalCode].join(' ')}
				</V2L2WorkItemDetailContentItem>
				<V2L2WorkItemDetailContentItem span={2}>
					<L1ReasonableButton
						color={FusionColor.White}
						icon={faDiamondTurnRight}
						label="Directions"
						onClick={() => window.location.href = appointmentMapUrl(appt)}
					/>
				</V2L2WorkItemDetailContentItem>
				<V2L2WorkItemDetailGuidContentItem label="Encounter GUID" value={dexp.encounterGuid} />
				<V2L2WorkItemDetailContentItem label="Encounter Status">
					{dexp.codeNext}
				</V2L2WorkItemDetailContentItem>
				<V2L2WorkItemDetailGuidContentItem label="Branch GUID" value={dexp.branchGuid} />
				{pendingSync ? (
					<V2L2WorkItemDetailContentItem label="Cloud Sync Status" icon={faCloudArrowUp}>
						Pending
					</V2L2WorkItemDetailContentItem>
				) : (
					<V2L2WorkItemDetailContentItem label="Cloud Sync Status" icon={faCircleCheck}>
						In Sync
					</V2L2WorkItemDetailContentItem>
				)}
				<V2L2WorkItemDetailContentItem label="Last Saved Time">
					{dexp.lastUpdateDt ?? '—'}
				</V2L2WorkItemDetailContentItem>
			</V2L2WorkItemDetailContent>
			<V2L2WorkItemDetailActions>
				{canViewForm ? (
					<L1ReasonableButton
						color={FusionColor.White}
						icon={faGlasses}
						label="View Form"
						onClick={() => onActionClick(WorkItemAction.ViewForm)}
					/>
				): null}
				<V2L2VideoBobJobber
					open={videoBobJobberOpen}
					canJoinVideo={canJoinVideo}
					isJoiningVideo={isJoiningVideo}
					canStartForm={canStartEncounter}
					canContinueForm={canContinueForm}
					canSendReminder={canSendReminder}
					reminderState={reminderState}
					onActionClick={onActionClick}
				>
					{canStartEncounter ? (
						<L1ReasonableButton
							icon={faPlay}
							label="Start Encounter"
							onClick={(e) => {
								// prevent incrementBackgroundTaps
								// from closing the BobJobber immediately
								e.stopPropagation()
								onActionClick(WorkItemAction.StartEncounter)
							}}
							disabled={videoBobJobberOpen}
						/>
					) : null}
					{canContinueForm ? (
						<L1ReasonableButton
							icon={faPlay}
							label="Continue Form"
							onClick={(e) => {
								// prevent incrementBackgroundTaps
								// from closing the BobJobber immediately
								e.stopPropagation()
								onActionClick(WorkItemAction.ContinueForm)
							}}
							disabled={videoBobJobberOpen}
						/>
					) : null}
				</V2L2VideoBobJobber>
				<Spacer />
				{canCancel ? (
					<L1ReasonableButton
						color={FusionColor.StopRed}
						icon={faBan}
						label="Cancel"
						onClick={() => onActionClick(WorkItemAction.CancelAppointment)}
					/>
				) : null}
			</V2L2WorkItemDetailActions>
		</V2L2WorkItemDetailRoot>
	)
}
